import { Router } from "@solidjs/router";
import { FileRoutes } from "@solidjs/start/router";
import { MetaProvider, Title } from "@solidjs/meta";
import { Suspense, ErrorBoundary, onMount, Show } from "solid-js";
import { Footer } from "~/components";
import { UserProvider } from "~/_context/UserProvider";

import "./app.css";
import { sendDevEmail } from "~/_server";
import { isDev } from "./lib";

const AppShell = (props: { children: any }) => (
	<UserProvider>
		<MetaProvider>
			<Title>RIAS-regs</Title>
			<Suspense>{props.children}</Suspense>
			<Footer />
		</MetaProvider>
	</UserProvider>
);

export default () => (
	<Router
		root={props =>
			isDev
				? <AppShell>{props.children}</AppShell>
				: <ErrorBoundary fallback={(e, r) => <ErrorDisplay error={e} reset={r} />}>
					<AppShell>{props.children}</AppShell>
				</ErrorBoundary>
		}
	>
		<FileRoutes />
	</Router>
);

// A fetch that never reached the server: the user navigated away mid-request,
// their connection dropped, or an extension blocked it. Each browser words it
// differently and none of them attach a stack, which is why these reports
// arrived as "NetworkError ... Stack trace:" with nothing underneath.
const isConnectionError = ( error: any ) =>
	/NetworkError when attempting|Failed to fetch|Load failed|network request failed|The operation was aborted/i
		.test( String( error?.message ?? error ?? "" ) );

const ErrorDisplay = ( props: { error: any, reset: () => void } ) => {
	const connectionLost = () => isConnectionError( props.error );

	onMount( () => {
		if ( connectionLost() ) return; // transient - not worth an email

		const e = props.error;
		const lines = [
			`An error occurred: ${String( e?.message ?? e )}`,
			``,
			`Type: ${e?.name ?? typeof e}`,
			`Page: ${window.location.href}`,
			`When: ${new Date().toISOString()}`,
			`Browser: ${navigator.userAgent}`,
			``,
			`Stack trace:`,
			e?.stack ? String( e.stack ) : "(none)",
		];

		sendDevEmail( "Error", lines.join( "\n" ) ).catch( () => { /* offline - nothing to do */ } );
	} );

	return (
		<div class="p-0 m-0 max-w-225 bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded relative mx-auto my-56 flex flex-col items-center justify-center">
			<Show when={connectionLost()} fallback={
				<>
					<h1 class="text-3xl font-bold mb-4">Sorry, but something went wrong on RIAS-regs</h1>

					<h2 class="text-xl mb-2">Error: {props.error?.message}</h2>

					<p class="my-10 text-center">The development team will be notified, but please try again now or later.</p>
				</>
			}>
				<h1 class="text-3xl font-bold mb-4">Connection lost</h1>

				<p class="my-10 text-center">RIAS-regs could not reach the server. Check your connection, then try again.</p>
			</Show>

			<button type="button" class="" onClick={props.reset}>Try Again</button>
		</div>
	)
};
